/**
 * As telas que não vêm no primeiro pedaço.
 *
 * O pacote era um arquivo só de 831 KB de JS e 410 KB de CSS, e quem abre o app
 * no celular baixava as quarenta e seis telas para ver a porta de senha. Nenhuma
 * dependência de fora pesa aqui — o `package.json` tem React, o roteador e o
 * `shared` — então o peso é código nosso espalhado por tela, e a única ferramenta
 * que corta isso é o `import()` tardio.
 *
 * Ficam de fora daqui, de propósito, as cinco que aparecem antes de qualquer
 * toque: a `Entrada` e o `Comecar` (a porta), a `Moldura` e o `Painel` (o que
 * abre assim que a senha confere) e a `TarjaDeSessao`, que mora fora das rotas.
 * O `Kovu` também fica: o gato boia no canto do painel, e um toque nele não pode
 * esperar rede.
 *
 * A lista mora num arquivo só porque ela tem dois usos — partir e aquecer — e
 * duas listas de trinta e oito linhas divergem na primeira tela nova que alguém
 * acrescentar num lugar e esquecer no outro.
 */

import { lazy, type ComponentType } from 'react';

/**
 * Os mesmos `import()` de baixo, guardados na ordem em que foram declarados,
 * para o `aquecer()` poder chamá-los depois. É efeito colateral de fábrica, sim,
 * e é justamente o que impede a lista de existir duas vezes.
 */
const importadores: Array<() => Promise<unknown>> = [];

/**
 * Um `lazy` que entende export nomeado. Todas as telas daqui exportam pelo nome
 * (`export function Cinema`), e o `React.lazy` quer um `default`; esta é a ponte.
 * O `P` sai do próprio módulo, então `<Leitor ouvindo />` continua conferido
 * pelo TypeScript como antes.
 */
function tela<N extends string, P>(carregar: () => Promise<Record<N, ComponentType<P>>>, nome: N) {
  importadores.push(carregar);
  return lazy(async () => ({ default: (await carregar())[nome] }));
}

/* As três que saem do Kovu, e as duas listas do painel. */
export const KovuHoje = tela(() => import('./KovuHoje'), 'KovuHoje');
export const Quiz = tela(() => import('./Quiz'), 'Quiz');
export const Caderno = tela(() => import('./Caderno'), 'Caderno');
export const Lembrancas = tela(() => import('./Lembrancas'), 'Lembrancas');
export const Desejos = tela(() => import('./Desejos'), 'Desejos');

/* A cozinha inteira: o livro, a semana, o mercado e o que tem em casa. */
export const Receitas = tela(() => import('./Receitas'), 'Receitas');
export const Receita = tela(() => import('./Receita'), 'Receita');
export const Cardapio = tela(() => import('./Cardapio'), 'Cardapio');
export const Compras = tela(() => import('./Compras'), 'Compras');
export const Despensa = tela(() => import('./Despensa'), 'Despensa');

/* O resto do que mora dentro da moldura. */
export const Calendario = tela(() => import('./Calendario'), 'Calendario');
export const Calendarios = tela(() => import('./Calendarios'), 'Calendarios');
export const Galeria = tela(() => import('./Galeria'), 'Galeria');
export const Alexa = tela(() => import('./Alexa'), 'Alexa');
export const Financas = tela(() => import('./Financas'), 'Financas');
export const Cartas = tela(() => import('./Cartas'), 'Cartas');
export const Ajustes = tela(() => import('./Ajustes'), 'Ajustes');

/* As telas cheias soltas na raiz. */
export const Refugio = tela(() => import('./Refugio'), 'Refugio');
export const Quadro = tela(() => import('./Quadro'), 'Quadro');
export const Jardim = tela(() => import('./Jardim'), 'Jardim');

/* O cinema, a maior das seções: 456 KB de fonte em quatro telas. */
export const Cinema = tela(() => import('./cinema/Cinema'), 'Cinema');
export const Sessao = tela(() => import('./cinema/Sessao'), 'Sessao');
export const Baixar = tela(() => import('./cinema/Baixar'), 'Baixar');
export const Tocador = tela(() => import('./cinema/Tocador'), 'Tocador');

/* O AnimeX. */
export const Anime = tela(() => import('./anime/Anime'), 'Anime');

/* A biblioteca: 288 KB, cinco abas e o leitor. */
export const Livros = tela(() => import('./livros/Livros'), 'Livros');
export const Detalhe = tela(() => import('./livros/Detalhe'), 'Detalhe');
export const Leitor = tela(() => import('./livros/Leitor'), 'Leitor');
export const Ouvir = tela(() => import('./livros/Ouvir'), 'Ouvir');
export const NossosLivros = tela(() => import('./livros/NossosLivros'), 'NossosLivros');

/* A academia e a zona de jogos. */
export const Academia = tela(() => import('./academia/Academia'), 'Academia');
export const Estudo = tela(() => import('./academia/Estudo'), 'Estudo');
export const Tabuleiro = tela(() => import('./academia/Tabuleiro'), 'Tabuleiro');
export const Podio = tela(() => import('./academia/Podio'), 'Podio');
export const Jogos = tela(() => import('./jogos/Jogos'), 'Jogos');
export const PingPong = tela(() => import('./jogos/PingPong'), 'PingPong');
export const Sala = tela(() => import('./jogos/Sala'), 'Sala');
export const Dupla = tela(() => import('./jogos/Dupla'), 'Dupla');

/**
 * Busca todos os pedaços em segundo plano, um de cada vez.
 *
 * Sem isto, partir o pacote troca um problema por outro: a primeira abertura
 * fica leve e **cada toque** passa a esperar rede, que num celular ruim é uma
 * espera visível onde hoje não há nenhuma. Com isto, a porta pinta com o pedaço
 * pequeno e o resto chega enquanto a pessoa olha o painel; quando ela toca em
 * Cinema, o pedaço já está no cache do navegador e o `Suspense` nem aparece.
 *
 * Um de cada vez, e não todos de uma vez, porque trinta e oito pedidos
 * simultâneos num celular disputam a mesma rede com o que o painel ainda está
 * carregando. Em sequência isso termina rápido e não atrapalha ninguém.
 *
 * Falha de rede aqui não é erro: o pedaço será pedido de novo quando a tela for
 * aberta de verdade, e aí sim o `Suspense` e o erro aparecem para quem está
 * olhando. Por isso o `catch` vazio.
 */
export async function aquecer(): Promise<void> {
  // Quem pediu economia de dados não quer que o app baixe o que ele não abriu.
  const rede = (navigator as { connection?: { saveData?: boolean } }).connection;
  if (rede?.saveData) return;

  for (const carregar of importadores) {
    try {
      await carregar();
    } catch {
      // De propósito: ver o comentário acima.
    }
  }
}

/**
 * Chama o `aquecer()` quando o navegador estiver à toa, para não disputar com a
 * primeira pintura. `requestIdleCallback` não existe no Safari antigo, então o
 * `setTimeout` é a rede de segurança — um segundo é depois de qualquer pintura.
 */
export function aquecerQuandoSobrar(): () => void {
  const ocioso = (
    window as typeof window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    }
  ).requestIdleCallback;

  if (ocioso) {
    const id = ocioso(() => void aquecer());
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(() => void aquecer(), 1_000);
  return () => window.clearTimeout(id);
}
